import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import api from '../../services/api';
import Spinner from '../common/Spinner';

const AlterarSenhaGmailModal: React.FC = () => {
  const { setRequerSenhaGmail, usuario } = useAuth();
  const [senhaGmail, setSenhaGmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Verificar se usuário já tem senha do Gmail ao montar o componente
  useEffect(() => {
    const verificarSenhaGmail = async () => {
      try {
        // Buscar dados atualizados do backend
        const response = await api.get('/auth/me');
        if (response.data.success && response.data.usuario) {
          const usuarioAtualizado = response.data.usuario;
          const temSenhaGmail = usuarioAtualizado.senha_app_gmail && usuarioAtualizado.senha_app_gmail.trim() !== '';
          
          if (temSenhaGmail) {
            // Se já tem senha, fechar modal e recarregar
            setRequerSenhaGmail(false);
            window.location.reload();
          }
        }
      } catch (error) {
        console.error('Erro ao verificar senha Gmail:', error);
      }
    };
    
    verificarSenhaGmail();
  }, [setRequerSenhaGmail]);

  const handleConfigurar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null);

    // Validação
    if (!senhaGmail) {
      setErro('A senha de app do Gmail é obrigatória');
      return;
    }

    // Remover espaços da senha
    const senhaLimpa = senhaGmail.replace(/\s/g, '');

    if (senhaLimpa.length !== 16) {
      setErro('A senha de app do Gmail deve ter exatamente 16 caracteres');
      return;
    }

    // Validar formato: deve conter apenas letras minúsculas (padrão Google)
    const formatoValido = /^[a-z]{16}$/;
    if (!formatoValido.test(senhaLimpa)) {
      setErro('Formato inválido! A senha de app do Gmail deve conter apenas letras minúsculas (exemplo: mvdz ihcw lqcu vegz). Não use sua senha pessoal do Gmail!');
      return;
    }

    try {
      setLoading(true);
      setErro(null);
      
      // Chamar a API para atualizar a senha do Gmail
      const response = await api.put('/auth/perfil', {
        senha_app_gmail: senhaLimpa
      });

      if (response.data.success) {
        toast.success('Senha de app do Gmail configurada com sucesso!');
        setRequerSenhaGmail(false);
        
        // Recarregar a página para atualizar o estado do usuário
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        setErro(response.data.message || 'Erro ao configurar senha do Gmail');
      }
    } catch (error: any) {
      console.error('Erro ao configurar senha do Gmail:', error);
      setErro(error.response?.data?.message || 'Erro ao configurar senha. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigurarDepois = () => {
    setRequerSenhaGmail(false);
    toast.info('Você pode configurar a senha do Gmail depois no seu perfil.');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="mb-6 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Configure sua Senha de App do Gmail</h2>
          <p className="text-gray-600">
            Para enviar notificações por email através da sua conta Gmail, configure uma senha de aplicativo.
          </p>
        </div>

        {erro && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {erro}
          </div>
        )}

        <form onSubmit={handleConfigurar}>
          <div className="mb-4">
            <label htmlFor="senha-gmail" className="block text-sm font-medium text-gray-700 mb-1">
              Senha de App do Gmail (16 caracteres)
            </label>
            <input
              id="senha-gmail"
              type="text"
              value={senhaGmail}
              onChange={(e) => setSenhaGmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-bravo-primary focus:border-bravo-primary font-mono"
              placeholder="mvdz ihcw lqcu vegz"
              disabled={loading}
              autoFocus
              maxLength={19}
            />
            <p className="text-xs text-gray-500 mt-1">
              Formato: 4 blocos de 4 letras minúsculas (exemplo: <span className="font-mono font-semibold">mvdz ihcw lqcu vegz</span>)
            </p>
            <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-xs text-yellow-800">
                <strong>⚠️ Atenção:</strong> NÃO use sua senha pessoal do Gmail! Use apenas a senha de app gerada pelo Google.
              </p>
            </div>
          </div>

          <div className="mb-6 p-4 bg-blue-50 rounded-md border border-blue-200">
            <h4 className="text-sm font-medium text-blue-800 mb-3">Como obter uma senha de app do Gmail?</h4>
            <ol className="text-xs text-blue-700 list-decimal pl-4 space-y-2">
              <li>
                <strong>Acesse sua conta Google:</strong>
                <a 
                  href="https://myaccount.google.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 underline ml-1 hover:text-blue-800"
                >
                  Clique aqui para acessar sua Conta Google
                </a>
              </li>
              <li>
                <strong>Ative a Verificação em duas etapas:</strong>
                <a 
                  href="https://myaccount.google.com/signinoptions/two-step-verification" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 underline ml-1 hover:text-blue-800"
                >
                  Clique aqui para ativar
                </a>
                <span className="block mt-1 text-orange-600 font-medium">⚠️ Este passo é obrigatório para gerar senhas de app</span>
              </li>
              <li>
                <strong>Gere uma senha de app:</strong>
                <a 
                  href="https://myaccount.google.com/apppasswords" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 underline ml-1 hover:text-blue-800"
                >
                  Clique aqui para criar uma senha de app
                </a>
              </li>
              <li>
                <strong>Configure a senha de app:</strong>
                <ul className="list-disc pl-4 mt-1 space-y-1">
                  <li>Digite "BravoHub" no campo de nome quando solicitado</li>
                  <li>Clique em "Criar"</li>
                  <li>Copie a senha gerada de 16 caracteres (sem espaços) e cole no campo acima</li>
                  <li>Clique em "Concluído" para finalizar</li>
                </ul>
              </li>
            </ol>
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-xs text-gray-700">
                <strong className="text-yellow-700">ℹ️ Importante:</strong> Esta senha será usada para enviar notificações por email através da sua conta Gmail, 
                evitando o limite de 500 emails/dia do servidor compartilhado. Suas credenciais são armazenadas com segurança e nunca são compartilhadas.
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="px-4 py-3 bg-bravo-primary text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bravo-primary transition-colors flex items-center justify-center font-semibold min-w-[160px]"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner size={18} className="!text-white mr-2" />
                  Configurando...
                </>
              ) : 'Configurar Agora'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AlterarSenhaGmailModal;
